/**
 * Backup API client — customer `/my/backups` (Phase 1: backup-api foundation
 * + R3 ad-hoc DB backups + R4 targets/CRUD + R5 restore preview/confirm).
 *
 * Mirrors the unified-envelope conventions used in `cluster-api.js`,
 * `billing-api.js`, and `uts-api.js`:
 *   - one axios instance off `envConfig`
 *   - best-effort Bearer attach via the request interceptor (the page route
 *     itself is auth-gated; this is defense in depth)
 *   - `unwrap(response)` resolves `{ success, message, data, ... }` into the
 *     bare `data` payload and throws on `success: false`.
 *
 * Three typed errors let the UI render specific copy without sniffing status
 * codes in components:
 *   - `BackupBridgeNotReady` — 503 from `GET /backup/database/instances` when
 *     cluster-api hasn't shipped the R6 DB-discovery bridge yet. The schedule
 *     wizard falls back to a manual host/port form.
 *   - `BackupAdapterStub` — 501 from POST/PUT against an S3 / GCS target
 *     because no object-storage upload adapter is wired in v1. The targets
 *     tab renders a "coming soon" badge on those rows; the schedule wizard
 *     surfaces a hint when one of those targets is selected.
 *   - `RestoreConfirmationRejected` — 400 from `POST /backup/{job_id}/restore`
 *     when the HMAC `confirmation_token` is expired / mismatched / bound to a
 *     different target. The restore modal re-fetches the preview and asks the
 *     user to re-type the RESTORE word.
 *
 * Job archive download is a Blob save-as (same shape as
 * `billing-api.downloadInvoice` — we deliberately do NOT `window.open` the
 * download URL because that drops the Bearer token).
 */
import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig, isBackupsEnabled } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'
import { normalizeScheduleRow, normalizeJobRow } from '@/utils/backup-rows'
import { isRouteNotServed } from '@/utils/service-reach'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

const ROOT = API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`

const backupAxios = axios.create({
  baseURL: ROOT,
  // Some routes (e.g. ad-hoc DB backup, restore) trigger a short K8s job
  // server-side; give them a generous ceiling. The standard /schedules CRUD
  // endpoints still complete in <1s.
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT || 120000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// ── Typed errors ────────────────────────────────────────────────────────────

/**
 * Raised when this face has no backup-api behind `/api/backup`: either the
 * face says so (`VUE_APP_BACKUPS_ENABLED=false` — the request is never sent)
 * or the answer is a non-JSON 404 from the website's own nginx (backup-api
 * answers every 404 with JSON). Not a failure of the user's data: the store
 * renders "not available on this environment" and stops asking.
 */
export class BackupServiceUnavailable extends Error {
  constructor(message) {
    super(message || 'Backups are not available on this environment')
    this.name = 'BackupServiceUnavailable'
    this.serviceUnavailable = true
  }
}

backupAxios.interceptors.request.use((config) => {
  if (!isBackupsEnabled()) {
    // Never put a request on the wire that can only 404 (and log a console
    // error the page cannot suppress).
    return Promise.reject(new BackupServiceUnavailable())
  }
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

backupAxios.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(isRouteNotServed(error) ? new BackupServiceUnavailable() : error)
)

/**
 * Raised when `GET /backup/database/instances` returns 503 because the
 * cluster-api → backup-api DB-discovery bridge (R6) isn't deployed yet.
 * Envelope shape:
 *   { error: "bridge_not_ready", message, details?: { ... } }
 */
export class BackupBridgeNotReady extends Error {
  constructor(message, details = {}) {
    super(message || 'Managed-database discovery is not yet available')
    this.name = 'BackupBridgeNotReady'
    this.bridgeNotReady = true
    this.detail = details
  }
}

/**
 * Raised when a target adapter is stubbed (today: S3 / GCS upload + download
 * return 501). Envelope shape:
 *   { error: "adapter_not_implemented", message, details?: { adapter, type } }
 */
export class BackupAdapterStub extends Error {
  constructor(message, details = {}) {
    super(message || 'This target type is not yet supported')
    this.name = 'BackupAdapterStub'
    this.adapterStub = true
    this.adapter = details.adapter || details.type || null
    this.detail = details
  }
}

/**
 * Raised when `POST /backup/{job_id}/restore` is rejected because the HMAC
 * confirmation_token is missing/expired/mismatched. Envelope shape:
 *   { error: "confirmation_rejected", message, details?: { reason } }
 */
export class RestoreConfirmationRejected extends Error {
  constructor(message, details = {}) {
    super(message || 'Restore confirmation token was rejected — please re-confirm')
    this.name = 'RestoreConfirmationRejected'
    this.confirmationRejected = true
    this.reason = details.reason || null
    this.detail = details
  }
}

// ── Envelope helpers ────────────────────────────────────────────────────────

function unwrap(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

// Row normalisers (rb-0815 Gate 3): applied once, at the service boundary,
// so the store + MyBackups.vue keep reading `id` / `name` / `started_at` /
// `finished_at` and Enable/Run-now never POSTs `/schedules/undefined/...`.
// Implementation lives in utils/backup-rows.js so `node --test tests/unit/`
// can exercise it without the axios/store import chain.

/**
 * Translate a 503 from `/backup/database/instances` into the typed
 * BackupBridgeNotReady error. Falls through for any other failure.
 */
function rethrowBridgeNotReady(error) {
  if (error?.response?.status === 503) {
    const body = error.response.data || {}
    const code = body.error || body.code
    if (code === 'bridge_not_ready' || code === 'bridge_unavailable' || body.message?.includes?.('bridge')) {
      throw new BackupBridgeNotReady(
        body.message || 'Managed-database discovery is not yet available',
        body.details || body.detail || {}
      )
    }
  }
  throw error
}

/**
 * Translate a 501 from a target endpoint into the typed BackupAdapterStub.
 */
function rethrowAdapterStub(error) {
  if (error?.response?.status === 501) {
    const body = error.response.data || {}
    throw new BackupAdapterStub(
      body.message || 'This target type is not yet supported',
      body.details || body.detail || {}
    )
  }
  throw error
}

/**
 * Translate a 400 from `POST /restore` carrying `error: "confirmation_rejected"`
 * into the typed RestoreConfirmationRejected.
 */
function rethrowRestoreRejected(error) {
  if (error?.response?.status === 400) {
    const body = error.response.data || {}
    const code = body.error || body.code
    if (code === 'confirmation_rejected' || code === 'invalid_confirmation_token') {
      throw new RestoreConfirmationRejected(
        body.message || 'Restore confirmation token was rejected',
        body.details || body.detail || {}
      )
    }
  }
  throw error
}

/**
 * Stream a backup-job archive as a Blob and trigger Save-As. Mirrors the
 * exact pattern in `billing-api.downloadInvoice`: blob fetch → object URL →
 * hidden anchor click → revoke. We deliberately do NOT `window.open()` the
 * download URL because that drops the Bearer token.
 *
 * Returns the suggested filename so the caller can surface it in a toast.
 */
async function streamArchiveDownload(jobId, suggestedName) {
  const response = await backupAxios.get(
    `/backup/${encodeURIComponent(jobId)}/download`,
    { responseType: 'blob' }
  )
  let filename = suggestedName || `backup-${jobId}.tar.gz`
  const dispo = response.headers?.['content-disposition'] || ''
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo)
  if (m && m[1]) {
    try {
      filename = decodeURIComponent(m[1])
    } catch (e) {
      filename = m[1]
    }
  }

  const ct = response.headers?.['content-type'] || 'application/octet-stream'
  const blob = new Blob([response.data], { type: ct })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
  return filename
}

// ── Public surface ──────────────────────────────────────────────────────────

export const backupApi = {
  // ── Schedules ─────────────────────────────────────────────────────────────
  /**
   * List backup schedules. All filter params are optional.
   *   type:    'database' | 'files' | 'critical' | 'full' | 'databases'
   *   kind:    'mongo' | 'mysql' | 'postgres' | 'redis'  (when type=database)
   *   enabled: true | false
   */
  async listSchedules({ type, kind, enabled, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize }
    if (type) params.type = type
    if (kind) params.kind = kind
    if (enabled !== undefined && enabled !== null) params.enabled = enabled
    const response = await backupAxios.get('/backup/schedules', { params })
    const body = response?.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Failed to load schedules')
        err.response = response
        throw err
      }
      return {
        items: (Array.isArray(body.data) ? body.data : []).map(normalizeScheduleRow),
        count: body.count ?? (Array.isArray(body.data) ? body.data.length : 0),
        pagination: body.meta?.pagination || null
      }
    }
    return {
      items: (Array.isArray(body) ? body : []).map(normalizeScheduleRow),
      count: 0,
      pagination: null
    }
  },

  async getSchedule(id) {
    const response = await backupAxios.get(
      `/backup/schedules/${encodeURIComponent(id)}`
    )
    return normalizeScheduleRow(unwrap(response))
  },

  /**
   * Body shape varies by type:
   *   - type:"database" → { type, kind, target_app, cron, retention, enabled,
   *                         name, target_ref?, engine_options? }
   *   - type:"files"    → { type, selection:[{pv, paths:[...]}], cron,
   *                         retention, enabled, name, target_ref? }
   *   - type:"critical" | "full" | "databases" (legacy/foundation) →
   *                         { type, cron, retention, enabled, name, target_ref? }
   */
  async createSchedule(payload) {
    const response = await backupAxios.post('/backup/schedules', payload)
    return normalizeScheduleRow(unwrap(response))
  },

  async updateSchedule(id, payload) {
    const response = await backupAxios.put(
      `/backup/schedules/${encodeURIComponent(id)}`,
      payload
    )
    return normalizeScheduleRow(unwrap(response))
  },

  async deleteSchedule(id) {
    const response = await backupAxios.delete(
      `/backup/schedules/${encodeURIComponent(id)}`
    )
    return unwrap(response)
  },

  async runNow(id) {
    const response = await backupAxios.post(
      `/backup/schedules/${encodeURIComponent(id)}/run-now`
    )
    return normalizeJobRow(unwrap(response))
  },

  async enableSchedule(id) {
    const response = await backupAxios.post(
      `/backup/schedules/${encodeURIComponent(id)}/enable`
    )
    return normalizeScheduleRow(unwrap(response))
  },

  async disableSchedule(id) {
    const response = await backupAxios.post(
      `/backup/schedules/${encodeURIComponent(id)}/disable`
    )
    return normalizeScheduleRow(unwrap(response))
  },

  // ── Jobs / history ────────────────────────────────────────────────────────
  async listJobs({ status, scheduleId, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize }
    if (status) params.status = status
    if (scheduleId) params.schedule_id = scheduleId
    const response = await backupAxios.get('/backup/jobs', { params })
    const body = response?.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Failed to load jobs')
        err.response = response
        throw err
      }
      return {
        items: (Array.isArray(body.data) ? body.data : []).map(normalizeJobRow),
        count: body.count ?? (Array.isArray(body.data) ? body.data.length : 0),
        pagination: body.meta?.pagination || null
      }
    }
    return {
      items: (Array.isArray(body) ? body : []).map(normalizeJobRow),
      count: 0,
      pagination: null
    }
  },

  async getJob(jobId) {
    const response = await backupAxios.get(
      `/backup/jobs/${encodeURIComponent(jobId)}`
    )
    return normalizeJobRow(unwrap(response))
  },

  /**
   * Trigger a Save-As of the job's archive (tar/zip). Returns the filename
   * used so the caller can show it in a toast (e.g. "Saved backup-xxx.tar.gz").
   */
  async downloadJob(jobId, suggestedName) {
    try {
      return await streamArchiveDownload(jobId, suggestedName)
    } catch (error) {
      // S3 / GCS adapter stubs return 501 even for download. Wrap so the UI
      // can render the "coming soon" copy instead of a generic toast.
      rethrowAdapterStub(error)
    }
  },

  // ── Ad-hoc database backup (R3) ───────────────────────────────────────────
  /**
   * Body: { kind: "mongo"|"mysql"|"postgres"|"redis",
   *         target_app: <ref>, target_ref?, engine_options? }
   */
  async runDatabaseBackup(payload) {
    const response = await backupAxios.post('/backup/database', payload)
    return unwrap(response)
  },

  /**
   * Discover the customer's managed-DB instances via cluster-api bridge.
   * May 503 with `{error:"bridge_not_ready"}` until cluster-api R6 ships —
   * we surface that as the typed `BackupBridgeNotReady` so the schedule
   * wizard can fall back to a manual host/port form.
   */
  async listDatabaseInstances({ kind } = {}) {
    const params = {}
    if (kind) params.kind = kind
    try {
      const response = await backupAxios.get('/backup/database/instances', { params })
      const body = response?.data
      if (body && typeof body === 'object' && 'success' in body) {
        if (body.success === false) {
          const err = new Error(body.message || 'Failed to load DB instances')
          err.response = response
          throw err
        }
        return Array.isArray(body.data) ? body.data : []
      }
      return Array.isArray(body) ? body : []
    } catch (error) {
      rethrowBridgeNotReady(error)
    }
  },

  // ── Targets (per-org remote destinations, R4) ─────────────────────────────
  async listTargets() {
    const response = await backupAxios.get('/backup/targets')
    return unwrap(response) || []
  },

  /**
   * Body: { name, type: "qnap"|"nfs"|"rsync"|"s3"|"gcs",
   *         endpoint, credential_ref?, default?: boolean }
   */
  async createTarget(payload) {
    try {
      const response = await backupAxios.post('/backup/targets', payload)
      return unwrap(response)
    } catch (error) {
      rethrowAdapterStub(error)
    }
  },

  async updateTarget(id, payload) {
    try {
      const response = await backupAxios.put(
        `/backup/targets/${encodeURIComponent(id)}`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      rethrowAdapterStub(error)
    }
  },

  async deleteTarget(id) {
    const response = await backupAxios.delete(
      `/backup/targets/${encodeURIComponent(id)}`
    )
    return unwrap(response)
  },

  // ── Restore (R5) ──────────────────────────────────────────────────────────
  async listRestores({ status, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize }
    if (status) params.status = status
    const response = await backupAxios.get('/backup/restores', { params })
    const body = response?.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Failed to load restores')
        err.response = response
        throw err
      }
      return {
        items: Array.isArray(body.data) ? body.data : [],
        count: body.count ?? (Array.isArray(body.data) ? body.data.length : 0),
        pagination: body.meta?.pagination || null
      }
    }
    return { items: Array.isArray(body) ? body : [], count: 0, pagination: null }
  },

  async getRestore(restoreId) {
    const response = await backupAxios.get(
      `/backup/restores/${encodeURIComponent(restoreId)}`
    )
    return unwrap(response)
  },

  /**
   * Two-stage restore handshake step 1 — issues an HMAC-signed
   * `confirmation_token` with TTL 300s. The token is bound to `target_app`
   * for `new_instance` mode. Returns:
   *   { confirmation_token, expires_at, what, impact, mode }
   */
  async getRestorePreview(jobId) {
    const response = await backupAxios.get(
      `/backup/${encodeURIComponent(jobId)}/restore-preview`
    )
    return unwrap(response)
  },

  /**
   * Two-stage restore handshake step 2 — perform the restore. Admin-only.
   * Body: { mode: "in_place"|"new_instance", confirmation_token,
   *         target_app?, options? }
   *
   * Throws `RestoreConfirmationRejected` if the token was rejected (the UI
   * re-opens the preview and re-prompts for the typed RESTORE word) and
   * `BackupAdapterStub` for the S3 / GCS stubs.
   */
  async performRestore(jobId, payload) {
    try {
      const response = await backupAxios.post(
        `/backup/${encodeURIComponent(jobId)}/restore`,
        payload
      )
      return unwrap(response)
    } catch (error) {
      rethrowRestoreRejected(error)
      rethrowAdapterStub(error)
    }
  }
}

export default backupAxios

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(backupAxios)
